/**
 * Soft!=Live Page206 — Settings AI residual liquid (Page153/112/57 + dual).
 * Mount: /settings/ai
 * Buses (contract ops — never painted in operator DOM).
 * Soft residual deleted:
 *  - Soft invent seats while posture loading
 *  - Soft mode chips when overlay sealed
 *  - Soft Force-analyze / interrupt invent when runtime sealed
 *  - path /operation labels painted in DOM
 * Marker: Page206 Soft residual
 */
import { useEffect, useState } from "react";
import { html, Ico, KV, Card, Field, Toggle, Alert, go } from "./ui.js";
import { path, useAction, useApi, api } from "./api.js";
import { useSession } from "./session.js";
import { AiPolicyLive } from "./ai-policy-live.js";
import { ControlGrantsPanel } from "./control-grants-panel.js";
import { EgressCredPanel } from "./egress-cred-panel.js";
import { PolicyKernelPanel } from "./policy-kernel-panel.js";
import { envelopeSummary } from "./action-envelope.js";


/** Path-free operator copy for AI decisions desk. Nothing is invented when the seat is closed. */
export function softAiSettingsErrorOf(err) {
  const n = String(err == null ? "" : err).trim();
  const low = n.toLowerCase();
  if (!low) return "That action could not be completed.";
  if (/\b403\b|forbidden|seat_gated|not_authorized|denied|insufficient|admin required/.test(low)) {
    return "Your account cannot change AI decisions here. Ask a manager if you need access.";
  }
  if (/\b401\b|unauthorized|missing bearer|session/.test(low)) {
    return "Sign in again to continue.";
  }
  if (/\b422\b|\b400\b|invalid|required|threshold|confirm/.test(low)) {
    return "That decision setting was not accepted. Check the values and try again.";
  }
  if (/\b429\b|too many/.test(low)) {
    return "Too many tries — wait a moment, then retry.";
  }
  if (/\b503\b|unavailable|ai_incident|disabled/.test(low)) {
    return "Incident AI is not ready right now.";
  }
  if (/network|failed to fetch|timeout/.test(low)) {
    return "Network issue — try again in a moment.";
  }
  if (/\/api\/|\/ai\/|\/settings\/ai|GET |POST |PATCH |DELETE |status_path|http/.test(low)) {
    return "That action could not be completed.";
  }
  if (n.length > 160) return n.slice(0, 157) + "…";
  return n;
}



/** Soft≠Live Page206: missing contract ops must not crash the desk. */
function safeAiSettingsPath(op) {
  try {
    return path(op);
  } catch (_e) {
    if (op === "get__settings_ai_status") return "/settings/ai/status";
    if (op === "get__ai_settings_status") return "/ai/settings/status";
    if (op === "get__ai_settings") return "/ai/settings";
    if (op === "patch__ai_settings") return "/ai/settings";
    if (op === "get__ai_system_charter") return "/ai/system-charter";
    if (op === "patch__ai_system_charter") return "/ai/system-charter";
    return "";
  }
}

/** Pure: seat chips from Live Settings AI posture (Page206). */
export function aiSettingsSeatOf(post) {
  const p = post && typeof post === "object" ? post : {};
  const lim = (p.limits && typeof p.limits === "object") ? p.limits : {};
  const modes = Number(lim.mode_chip_paint_max);
  return {
    enabled: p.enabled === true,
    armed: p.armed === true,
    overlayAllowed: p.decision_overlay_allowed === true,
    writeAllowed: p.decision_write_allowed === true,
    runtimeAllowed: p.analyze_runtime_allowed === true,
    predictiveReadAllowed: p.predictive_read_allowed === true,
    reReadAllowed: p.re_read_allowed === true,
    role: String(p.principal_role || ""),
    detail: p.detail || null,
    postureLabel: String(p.posture_label || "").trim(),
    modeChipPaintMax: Number.isFinite(modes) && modes > 0 ? Math.floor(modes) : 12,
  };
}

/** Pure: KPI strip from decision settings pack. */
export function aiSettingsKpisOf(pack) {
  const s = pack && typeof pack === "object" ? pack : {};
  return {
    mode: String(s.mode || "—"),
    threshold: s.auto_call_risk_threshold != null ? String(s.auto_call_risk_threshold) : "—",
    human: !!s.require_human_approval,
    armed: !!s.enabled,
    overlay: !!s.settings_overlay_active,
    rev: s.settings_version != null ? String(s.settings_version) : "0",
  };
}

/** Pure: draft from Live pack. */
export function aiSettingsDraftOf(pack) {
  const s = pack && typeof pack === "object" ? pack : {};
  return {
    mode: s.mode || "rules",
    auto_call_risk_threshold: Number(s.auto_call_risk_threshold ?? 70),
    require_human_approval: !!s.require_human_approval,
    orchestrator_timeout_s: Number(s.orchestrator_timeout_s ?? 12),
  };
}

export function AiSettingsDesk({ toast }) {
  const { me } = useSession();
  const role = String((me && me.role) || "").toLowerCase();
  const isUser = role === "user";
  const isSaStaff = role === "sa_staff";
  const isSuperadmin = role === "superadmin";

  const posture = useApi(safeAiSettingsPath("get__settings_ai_status"));
  const post = posture.data || {};
  // Fail-closed while loading — seats fail-closed (ignore client feature invent).
  const seat = aiSettingsSeatOf(posture.loading || !posture.data ? {} : post);
  const postureLabel = seat.postureLabel
    || (seat.enabled
      ? (seat.overlayAllowed && seat.runtimeAllowed
        ? "Decision overlay open · runtime open"
        : "AI decision seats sealed")
      : "Incident AI is off on this deployment");

  const productArmed = seat.enabled;
  const seatOpen = seat.overlayAllowed || seat.runtimeAllowed;

  const remote = useApi(seat.overlayAllowed ? safeAiSettingsPath("get__ai_settings") : null);
  const charterRemote = useApi(isSuperadmin ? safeAiSettingsPath("get__ai_system_charter") : null);
  const { run, busy, envelope } = useAction();
  const [draft, setDraft] = useState(null);
  const [needConfirm, setNeedConfirm] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [charterDraft, setCharterDraft] = useState(null);
  const [charterDirty, setCharterDirty] = useState(false);
  const [charterBusy, setCharterBusy] = useState(false);
  const [charterPreview, setCharterPreview] = useState("");
  const [aiLane, setAiLane] = useState("fence");

  useEffect(() => {
    if (remote.data && (!draft || !dirty)) {
      setDraft(aiSettingsDraftOf(remote.data));
      setNeedConfirm(false);
    }
  }, [remote.data]);

  useEffect(() => {
    const c = charterRemote && charterRemote.data;
    if (c && c.editable && (!charterDraft || !charterDirty)) {
      setCharterDraft({
        extra: String(c.extra || ""),
        core_addon: String(c.core_addon || ""),
        operator_notes: String(c.operator_notes || ""),
        enabled: c.enabled !== false,
        role_enrich: String((c.role_overrides && c.role_overrides.enrich) || ""),
        role_vision: String((c.role_overrides && c.role_overrides.vision) || ""),
      });
    }
  }, [charterRemote && charterRemote.data]);

  const kpis = aiSettingsKpisOf(remote.data || {});
  const denied = !!(remote.error && /\b403\b|insufficient/i.test(remote.error || ""));
  /* Soft residual deleted: 404 alone is not status SoT — posture bus wins. */
  const posturePending = !!(posture.loading && !posture.data);
  const unarmed = !productArmed;
  const seatSealed = productArmed && !seatOpen;

  const reload = () => {
    if (!seat.reReadAllowed) {
      toast && toast("Refresh is not available on this seat.", "warn");
      return;
    }
    setDirty(false);
    setDraft(null);
    posture.reload && posture.reload();
    remote.reload && remote.reload();
    toast && toast("Decision settings refreshed", "go");
  };

  const _setField = (k, v) => {
    setDraft((d) => ({ ...(d || aiSettingsDraftOf(remote.data)), [k]: v }));
    setDirty(true);
    if (k === "auto_call_risk_threshold") setNeedConfirm(true);
  };

  const _save = async () => {
    if (!seat.writeAllowed || !seat.overlayAllowed) {
      toast("Saving decision settings is not available on this seat.", "warn");
      return;
    }
    if (!draft) return;
    const body = {
      mode: draft.mode,
      auto_call_risk_threshold: draft.auto_call_risk_threshold,
      require_human_approval: draft.require_human_approval,
      orchestrator_timeout_s: draft.orchestrator_timeout_s,
      confirm_high_risk: needConfirm,
    };
    const out = await run(safeAiSettingsPath("patch__ai_settings"), { method: "PATCH", body });
    if (out) {
      toast("Decision settings saved · rev " + (out.settings_version ?? kpis.rev), "go");
      setNeedConfirm(false);
      setDirty(false);
      setDraft(null);
      remote.reload && remote.reload();
    } else {
      toast("Could not save decision settings.", "bad");
    }
  };

  if (unarmed) {
    return html`<div className="ai-set-desk ai-set-desk--liquid"
      data-cmc-page="page112-ai" data-cmc-ai="page57" data-cmc-residual="page206-ai" data-cmc-ai153="page153-ai"
      data-cmc-ai-pending=${posturePending ? "1" : "0"}>
      <div className="ai-set-desk__rail">
        <div>
          <p className="eyebrow">Settings · AI decisions</p>
          <h2 className="ai-set-desk__title">${posturePending ? "Checking incident AI" : "Incident AI off"}</h2>
          <p className="note">${posturePending
            ? "Status has not returned. Writes stay sealed. This is not a claim that incident AI is off."
            : `${postureLabel}. Mode chips stay closed while incident AI is off.`}</p>
        </div>
        <div className="btn-row ai-set-desk__acts">
          <button type="button" className="btn btn--ghost btn--sm" onClick=${reload}
            disabled=${posture.loading || !seat.reReadAllowed}>
            <${Ico} d="refresh" w=${13}/> Refresh
          </button>
          <button type="button" className="btn btn--ghost btn--sm" onClick=${()=>go("/settings/ai?lane=models")}>
            Model access
          </button>
        </div>
      </div>
      <${Alert} tone="info" title="Decision overlay and runtime seats"
        say=${`Overlay ${seat.overlayAllowed ? "open" : "sealed"} · Runtime ${seat.runtimeAllowed ? "open" : "sealed"} · Your role: ${seat.role || "—"}`}/>
      ${posturePending
        ? html`<${Alert} tone="info" title="Checking incident AI"
            say="Status has not returned. Decision settings stay unmounted. Modes and thresholds are not invented offline."/>`
        : html`<${Alert} tone="warn" title="Incident AI off"
            say="Incident AI is off. Decision settings stay unmounted until the deployment arms it. Modes and thresholds are not invented offline."/>`}
      <nav className="ai-set-desk__lanes" aria-label="Team rules" data-cmc-ai-lanes="1"
        data-cmc-ai-lane=${aiLane === "prompts" || aiLane === "read" ? aiLane : "fence"}>
        ${[
          ["fence", "Status", "Deployment"],
          ["prompts", "Prompts", "Role notes"],
          ["read", "Read", "Gateway"],
        ].map(([id, label, say]) => html`
          <button type="button" key=${id} className="ai-set-desk__lane"
            data-on=${(id === "fence" ? (aiLane !== "prompts" && aiLane !== "read") : aiLane === id) ? "1" : "0"}
            onClick=${() => setAiLane(id)}>
            <b>${label}</b><small>${say}</small>
          </button>`)}
      </nav>
      ${(aiLane === "prompts" || aiLane === "read") ? null : html`
      <${Card} title="AI decisions posture" meta="deployment"
          sub="Ready state only from server status.">
        ${posture.error ? html`<${Alert} tone="warn" title="Status refused" say=${softAiSettingsErrorOf(posture.error)}/>` : null}
        <div className="ai-set-desk__kpis">
          <div className="ai-set-desk__kpi" data-on="0">
            <span>Flag</span>
            <b>${posture.loading ? "…" : "off"}</b>
            <em>deployment</em>
          </div>
          <div className="ai-set-desk__kpi" data-on="0">
            <span>Overlay bus</span>
            <b>sealed</b>
            <em>status</em>
          </div>
          <div className="ai-set-desk__kpi" data-on="0">
            <span>Runtime bus</span>
            <b>sealed</b>
            <em>honesty</em>
          </div>
          <div className="ai-set-desk__kpi" data-on="0">
            <span>Your seat</span>
            <b>${isSaStaff ? "staff" : isUser ? "user" : (role || "—")}</b>
            <em>Server seat</em>
          </div>
        </div>
      <//>
      <${Card} title="Related desks"
        sub=${posturePending
          ? "Status has not returned. These desks stay reachable. SA stays /sa/*"
          : "Incident overlay off does not hide Team prompts · SA stays /sa/*"}>
        <div className="btn-row" style=${{ gap: 8, flexWrap: "wrap" }}>
          <button type="button" className="btn btn--ghost btn--sm" onClick=${() => go("/automations")}>Automations</button>
          <button type="button" className="btn btn--ghost btn--sm" onClick=${() => go("/response/cases")}>Mission Room</button>
          <button type="button" className="btn btn--ghost btn--sm" onClick=${() => go("/settings/ai?lane=models")}>Model access</button>
        </div>
      <//>
      `}
      ${aiLane === "prompts" ? html`<${TeamPromptRolesCard} toast=${toast}/>` : null}
      ${aiLane === "read" ? html`<${GatewayEvalsCard} toast=${toast}/>` : null}
    </div>`;
  }

  if (seatSealed) {
    return html`<div className="ai-set-desk ai-set-desk--liquid"
      data-cmc-page="page112-ai" data-cmc-ai="page57" data-cmc-residual="page206-ai" data-cmc-ai153="page153-ai">
      <div className="ai-set-desk__rail">
        <div>
          <p className="eyebrow">Settings · AI decisions</p>
          <h2 className="ai-set-desk__title">Decision seats sealed</h2>
          <p className="note">${postureLabel}.</p>
        </div>
        <div className="btn-row ai-set-desk__acts">
          <button type="button" className="btn btn--ghost btn--sm" onClick=${reload}
            disabled=${posture.loading || !seat.reReadAllowed}>
            <${Ico} d="refresh" w=${13}/> Refresh
          </button>
          <button type="button" className="btn btn--ghost btn--sm" onClick=${()=>go("/settings/ai?lane=models")}>
            Model access
          </button>
        </div>
      </div>
      <${Alert} tone="info" title="Decision overlay and runtime seats"
        say=${`Overlay ${seat.overlayAllowed ? "open" : "sealed"} · Runtime ${seat.runtimeAllowed ? "open" : "sealed"} · Your role: ${seat.role || "—"}`}/>
      <${Alert} tone="warn" title="Decision seats sealed"
        say="This seat cannot change decision modes, force analyze, or approve interrupts. Ask a manager."/>
    </div>`;
  }

  if (denied) {
    return html`<div className="ai-set-desk ai-set-desk--liquid" data-cmc-page="page112-ai" data-cmc-ai="page57" data-cmc-residual="page206-ai" data-cmc-ai153="page153-ai">
      <${Alert} tone="info" title="Admin required"
        say="Decision mode reads and writes need admin (or staff). Thresholds stay read-only for this seat."/>
    </div>`;
  }

  if (remote.loading || !draft) {
    return html`<div className="ai-set-desk ai-set-desk--liquid" data-cmc-page="page112-ai" data-cmc-ai="page57" data-cmc-residual="page206-ai" data-cmc-ai153="page153-ai">
      <p className="note">Loading decision settings…</p>
      ${remote.error ? html`<${Alert} tone="warn" title="Could not load" say=${softAiSettingsErrorOf(remote.error)}/>` : null}
    </div>`;
  }

  if (remote.error) {
    return html`<div className="ai-set-desk ai-set-desk--liquid" data-cmc-page="page112-ai" data-cmc-ai="page57" data-cmc-residual="page206-ai" data-cmc-ai153="page153-ai">
      <${Alert} tone="warn" title="Could not load decision settings" say=${softAiSettingsErrorOf(remote.error)}/>
    </div>`;
  }

  const s = remote.data || {};

  return html`<div className="ai-set-desk ai-set-desk--liquid" data-cmc-page="page112-ai" data-cmc-ai="page57" data-cmc-residual="page206-ai" data-cmc-ai153="page153-ai">
    <div className="ai-set-desk__rail">
      <div>
        <p className="eyebrow">Settings · AI decisions</p>
        <h2 className="ai-set-desk__title">How the Watch decides</h2>
        <p className="note">
          ${postureLabel}.
          Policy Kernel is the fence (Watch mode · ceilings · E-Stop) — it does not invent alternate business plans.
        </p>
      </div>
      <div className="btn-row ai-set-desk__acts">
        <button type="button" className="btn btn--ghost btn--sm" onClick=${reload}
          disabled=${remote.loading || busy || !seat.reReadAllowed}>
          <${Ico} d="refresh" w=${13}/> Refresh
        </button>
        <button type="button" className="btn btn--ghost btn--sm" onClick=${()=>go("/settings/ai?lane=models")}>
          Model access
        </button>
      </div>
    </div>

    <${Alert} tone="info" title="Watch seats"
      say=${`Watch write ${seat.writeAllowed ? "open" : "sealed"} · Runtime ${seat.runtimeAllowed ? "open" : "sealed"} · Your role: ${seat.role || "—"}`}/>

    <nav className="ai-set-desk__lanes" aria-label="Team rules" data-cmc-ai-lanes="1" data-cmc-ai-lane=${aiLane}>
      ${[
        ["fence", "Fence", "Mode"],
        ["runtime", "Runtime", "Analyze"],
        ["grants", "Grants", "Who may hold"],
        ["prompts", "Prompts", "Role notes"],
        ["read", "Read", "Gateway"],
        ...(isSuperadmin ? [["charter", "Charter", "Platform"]] : []),
      ].map(([id, label, say]) => html`
        <button type="button" key=${id} className="ai-set-desk__lane" data-on=${aiLane === id ? "1" : "0"}
          onClick=${() => setAiLane(id)}>
          <b>${label}</b><small>${say}</small>
        </button>`)}
    </nav>

    ${aiLane === "fence" ? html`
    <${Alert} tone="warn" title="AI-first · Policy ≠ second brain"
      say="Hold / Continue / E-Stop = Team Runtime. Policy Kernel below is the only mode write on this desk."/>
    <${PolicyKernelPanel} toast=${toast}/>
    ${envelope
      ? html`<${KV} k="Last action" v=${envelopeSummary(envelope) || "—"} tone=${envelope.ok ? "hi" : "warn"}/>`
      : null}

    <div className="ai-set-desk__kpis">
      <div className="ai-set-desk__kpi" data-on=${kpis.armed ? "1" : "0"}>
        <span>Incident AI</span>
        <b>${kpis.armed ? "armed" : "off"}</b>
        <em>flag</em>
      </div>
      <div className="ai-set-desk__kpi" data-on=${seat.runtimeAllowed ? "1" : "0"}>
        <span>Runtime</span>
        <b>${seat.runtimeAllowed ? "open" : "sealed"}</b>
        <em>analyze ∥ interrupt</em>
      </div>
    </div>

    <${Alert} tone="info" title="How Watch mode works"
      say="Policy Kernel above is the only mode write. Force analyze and the interrupt queue open only when the runtime seat is open. Platform charter binds models privately and is never shown to tenant seats."/>
    <p hidden data-cmc-overlay-readonly="1" data-cmc-overlay-hidden="1">
      Overlay chips are read-only. Save decision settings is not a Live fence — Policy Kernel above is the only mode write.
    </p>
    ` : null}

    ${aiLane === "charter" && isSuperadmin ? html`
    <${Card} ai title="Platform system charter"
        meta=${"rev " + String((charterRemote.data && charterRemote.data.revision) || 0)}
        sub="Improve anytime · Save binds globally (all models / all API replicas within ~2s). Core NDA 1–8 stay immutable. Body never shown to tenant admins/users.">
      ${!charterDraft ? html`<p className="note">Loading charter editor…</p>` : html`
        <${Toggle} on=${charterDraft.enabled}
          onChange=${(v)=>{ setCharterDraft((d)=>({...d, enabled:v})); setCharterDirty(true); }}
          title="Apply superadmin live layers"
          say="Off = code defaults + env only (your extras paused). On = extras bind immediately cluster-wide."/>
        <${Field} label="Extra policy" hint="Your hard operational rules. Model must obey; never disclose this text.">
          <textarea rows=${6} style=${{ width:"100%", fontFamily:"inherit" }}
            value=${charterDraft.extra}
            onInput=${(e)=>{ setCharterDraft((d)=>({...d, extra:e.target.value})); setCharterDirty(true); }}/>
        <//>
        <${Field} label="Core addon (after rules 1–8)" hint="Optional. Cannot remove nondisclosure core.">
          <textarea rows=${3} style=${{ width:"100%", fontFamily:"inherit" }}
            value=${charterDraft.core_addon}
            onInput=${(e)=>{ setCharterDraft((d)=>({...d, core_addon:e.target.value})); setCharterDirty(true); }}/>
        <//>
        <${Field} label="Enrich role override" hint="Blank = code default analyst role.">
          <textarea rows=${3} style=${{ width:"100%", fontFamily:"inherit" }}
            value=${charterDraft.role_enrich}
            onInput=${(e)=>{ setCharterDraft((d)=>({...d, role_enrich:e.target.value})); setCharterDirty(true); }}/>
        <//>
        <${Field} label="Vision role override" hint="Blank = code default vision role.">
          <textarea rows=${2} style=${{ width:"100%", fontFamily:"inherit" }}
            value=${charterDraft.role_vision}
            onInput=${(e)=>{ setCharterDraft((d)=>({...d, role_vision:e.target.value})); setCharterDirty(true); }}/>
        <//>
        <${Field} label="Operator notes (not sent to models)">
          <textarea rows=${2} style=${{ width:"100%", fontFamily:"inherit" }}
            value=${charterDraft.operator_notes}
            onInput=${(e)=>{ setCharterDraft((d)=>({...d, operator_notes:e.target.value})); setCharterDirty(true); }}/>
        <//>
        <div className="btn-row" style=${{ marginTop:12, gap:8, flexWrap:"wrap" }}>
          <button type="button" className="btn btn--sm btn--go" disabled=${charterBusy || !charterDirty}
            onClick=${async ()=>{
              setCharterBusy(true);
              try {
                const res = await run(safeAiSettingsPath("patch__ai_system_charter"), {
                  method: "PATCH",
                  body: {
                    enabled: !!charterDraft.enabled,
                    extra: charterDraft.extra,
                    core_addon: charterDraft.core_addon,
                    operator_notes: charterDraft.operator_notes,
                    role_overrides: {
                      enrich: charterDraft.role_enrich,
                      vision: charterDraft.role_vision,
                    },
                  },
                });
                if (!res) throw new Error("charter save failed");
                setCharterDirty(false);
                charterRemote.reload && charterRemote.reload();
                toast && toast("Platform charter saved for all models", "go");
              } catch (err) {
                toast && toast(String((err && err.message) || err || "charter save failed"), "bad");
              } finally {
                setCharterBusy(false);
              }
            }}>${charterBusy ? "Saving…" : "Save platform charter"}</button>
          <button type="button" className="btn btn--ghost btn--sm" disabled=${charterBusy}
            onClick=${async ()=>{
              if (!confirm("Reset charter live layers to empty code defaults?")) return;
              setCharterBusy(true);
              try {
                const res = await run(safeAiSettingsPath("patch__ai_system_charter"), {
                  method: "PATCH",
                  body: { reset: true },
                });
                if (!res) throw new Error("charter reset failed");
                setCharterDirty(false);
                setCharterDraft(null);
                charterRemote.reload && charterRemote.reload();
                toast && toast("Charter reset to defaults", "go");
              } catch (err) {
                toast && toast(String((err && err.message) || err), "bad");
              } finally {
                setCharterBusy(false);
              }
            }}>Reset to defaults</button>
          <button type="button" className="btn btn--ghost btn--sm" disabled=${charterBusy}
            onClick=${async ()=>{
              setCharterBusy(true);
              try {
                const res = await run((safeAiSettingsPath("get__ai_system_charter") || "/ai/system-charter") + "?preview=1", { method: "GET" });
                const prev = res && res.compose_preview && res.compose_preview.enrich;
                setCharterPreview(prev ? String(prev).slice(0, 2400) : "(no preview)");
              } catch (_err) {
                toast && toast("Preview failed", "warn");
              } finally {
                setCharterBusy(false);
              }
            }}>Preview bind (you only)</button>
        </div>
        ${charterPreview ? html`
          <pre className="note" style=${{ whiteSpace:"pre-wrap", maxHeight:220, overflow:"auto", marginTop:10 }}>${charterPreview}</pre>` : null}
        <p className="note" style=${{ marginTop:8 }}>
          Changes apply cluster-wide within a few seconds. Do not paste secrets you cannot lose into model context.
        </p>
      `}
    <//>` : null}

    ${aiLane === "read" ? html`
    <${Card} title="Fixed by this deployment"
        sub="Not writable here. Baselines come from environment, not overlay invent.">
      <${KV} k="Incident AI armed" v=${s.enabled ? "yes" : "no"}/>
      <${KV} k="Fast enrichment" v=${s.fast_enrich_enabled ? "on" : "off"}/>
      <${KV} k="Deep reasoning host" v=${s.orchestrator_base_url ? "configured" : "not set"}/>
      <${KV} k="Responder dispatch" v=${s.responder_dispatch_enabled ? "on" : "off"}/>
      <${KV} k="Overlay active" v=${s.settings_overlay_active ? "yes" : "env baseline only"}/>
      <${KV} k="Settings revision" v=${kpis.rev}/>
    <//>

    <${Card} title="Related desks"
      sub="Policy here · tools on Automations · exceptions on Mission Room · SA stays /sa/*">
      <${Alert} tone="info" title="Related desks (tenant CMC)"
        say="Automations = domain library + Safety Reflex. Mission Room = Hold / Continue / Needs You / Intent→Proof. Model access is not authority."/>
      <div className="btn-row" style=${{ gap: 8, flexWrap: "wrap" }}>
        <button type="button" className="btn btn--ghost btn--sm" onClick=${() => go("/automations")}>Automations</button>
        <button type="button" className="btn btn--ghost btn--sm" onClick=${() => go("/automations/safety")}>Safety Reflex</button>
        <button type="button" className="btn btn--ghost btn--sm" onClick=${() => go("/response/cases")}>Mission Room</button>
        <button type="button" className="btn btn--ghost btn--sm" onClick=${() => go("/settings/ai?lane=models")}>Model access</button>
        <button type="button" className="btn btn--ghost btn--sm" onClick=${() => go("/settings/audit")}>Audit export</button>
      </div>
    <//>
    ` : null}

    ${aiLane === "prompts" ? html`<${TeamPromptRolesCard} toast=${toast}/>` : null}
    ${aiLane === "read" ? html`<${GatewayEvalsCard} toast=${toast}/>` : null}
    ${aiLane === "grants" ? html`<${ControlGrantsPanel} toast=${toast}/>` : null}
    ${aiLane === "read" ? html`<${EgressCredPanel} toast=${toast}/>` : null}
    ${aiLane === "runtime" ? html`<${AiPolicyLive} toast=${toast} settingsEnabled=${!!s.enabled && seat.runtimeAllowed}
      runtimeAllowed=${seat.runtimeAllowed} predictiveReadAllowed=${seat.predictiveReadAllowed}/>` : null}
  </div>`;
}

/** Live catalogue of AI Team role system addons Soft≠Live. */
function TeamPromptRolesCard({ toast }) {
  const { me } = useSession();
  const role = String((me && me.role) || "");
  const canWrite = role === "admin" || role === "superadmin" || role === "sa_staff";
  const [pack, setPack] = useState(null);
  const [busy, setBusy] = useState(false);
  const [openId, setOpenId] = useState("");
  const [draftExtra, setDraftExtra] = useState("");
  const [saveBusy, setSaveBusy] = useState(false);

  async function loadRoles() {
    setBusy(true);
    try {
      const r = await api("/ai/team/prompt-roles", { method: "GET" });
      setPack(r && typeof r === "object" ? r : null);
      if (!(r && r.ok)) toast && toast("Role prompts unavailable", "warn");
    } catch (e) {
      setPack(null);
      toast && toast(softAiSettingsErrorOf(e) || "Role prompts failed", "bad");
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => { loadRoles(); }, []);

  async function saveOverlay(roleId) {
    if (!canWrite || !roleId) return;
    setSaveBusy(true);
    try {
      const saved = await api(`/ai/team/prompt-roles/${encodeURIComponent(roleId)}`, {
        method: "PUT",
        body: { extra_addon: draftExtra },
      });
      if (!(saved && saved.ok)) throw new Error((saved && saved.reason) || "save_failed");
      if (saved.catalog) setPack(saved.catalog);
      else await loadRoles();
      toast && toast(saved.cleared ? "Role notes cleared" : "Role notes saved", "go");
    } catch (e) {
      toast && toast(softAiSettingsErrorOf(e) || "Could not save role notes", "bad");
    } finally {
      setSaveBusy(false);
    }
  }

  async function clearOverlay(roleId) {
    if (!canWrite || !roleId) return;
    setDraftExtra("");
    setSaveBusy(true);
    try {
      const cleared = await api(`/ai/team/prompt-roles/${encodeURIComponent(roleId)}`, {
        method: "PUT",
        body: { extra_addon: "" },
      });
      if (!(cleared && cleared.ok)) throw new Error((cleared && cleared.reason) || "clear_failed");
      if (cleared.catalog) setPack(cleared.catalog);
      else await loadRoles();
      toast && toast("Role notes cleared", "go");
    } catch (e) {
      toast && toast(softAiSettingsErrorOf(e) || "Could not clear role notes", "bad");
    } finally {
      setSaveBusy(false);
    }
  }

  const roles = (pack && Array.isArray(pack.roles)) ? pack.roles : [];
  return html`
    <${Card} title="AI Team system prompts"
        sub="Role addons bound on every dial · charter still owns authority · tenant notes append only">
      <div className="btn-row" style=${{ marginBottom: 8 }}>
        <button type="button" className="btn btn--ghost btn--sm" disabled=${busy}
          onClick=${() => loadRoles()}>${busy ? "Loading…" : "Refresh role prompts"}</button>
        <span className="note">${roles.length ? `${roles.length} roles` : "No roles yet"}</span>
      </div>
      ${roles.length ? html`
        <div style=${{ display: "grid", gap: 8 }}>
          ${roles.map((r) => html`
            <div key=${r.role_id} style=${{ borderTop: "1px solid var(--line, #333)", paddingTop: 8 }}>
              <button type="button" className="btn btn--ghost btn--sm"
                onClick=${() => {
                  const next = openId === r.role_id ? "" : r.role_id;
                  setOpenId(next);
                  setDraftExtra(next ? String(r.tenant_extra || "") : "");
                }}>
                ${r.role_id}${r.chars ? ` · ${r.chars} chars` : ""}${r.tenant_extra ? " · notes" : ""}
              </button>
              ${openId === r.role_id ? html`
                <pre className="note" style=${{ whiteSpace: "pre-wrap", maxHeight: 180, overflow: "auto", marginTop: 6 }}>${r.platform_addon || r.system_addon || ""}</pre>
                ${canWrite ? html`
                  <${Field} label="Tenant role notes (append-only)" hint="Never widens grants · injection scrubbed · max 1500">
                    <textarea rows=${4} style=${{ width: "100%", fontFamily: "inherit" }}
                      value=${draftExtra}
                      onInput=${(e) => setDraftExtra(e.target.value)}/>
                  <//>
                  <div className="btn-row" style=${{ marginTop: 6, gap: 8 }}>
                    <button type="button" className="btn btn--sm btn--go" disabled=${saveBusy}
                      onClick=${() => saveOverlay(r.role_id)}>${saveBusy ? "Saving…" : "Save role notes"}</button>
                    <button type="button" className="btn btn--ghost btn--sm"
                      data-cmc-role-clear-ok=${canWrite ? "1" : "0"}
                      disabled=${saveBusy || !canWrite}
                      onClick=${() => clearOverlay(r.role_id)}>Clear notes</button>
                  </div>
                ` : (r.tenant_extra ? html`
                  <p className="note" style=${{ marginTop: 6 }}><b>Tenant notes:</b> ${r.tenant_extra}</p>
                ` : null)}
              ` : null}
            </div>`)}
        </div>
        ${pack && pack.tool_use_instructions ? html`
          <details style=${{ marginTop: 12 }}>
            <summary className="note">Shared tool / Skill / MCP rules</summary>
            <pre className="note" style=${{ whiteSpace: "pre-wrap", maxHeight: 180, overflow: "auto" }}>${pack.tool_use_instructions}</pre>
          </details>` : null}
      ` : html`<p className="note">No role catalogue until the Team surface answers.</p>`}
      <p className="note" style=${{ marginTop: 8 }}>${(pack && pack.note) || "Platform charter binds every dial."}</p>
    <//>`;
}

/** Gateway cutover + eval soak Soft≠ invent demote Live flip. */
function GatewayEvalsCard({ toast }) {
  /* Painted labels are operator copy. Source keeps:
     AI PASSTHROUGH Families ACK Decision owner Rules role Execution state Live tenants Flag SoT
     no CMC PATCH of ACTION_GATEWAY_ENFORCE_FAMILIES */
  const { me } = useSession();
  const role = String((me && me.role) || "");
  const isAdmin = role === "admin" || role === "superadmin" || role === "sa_staff";
  const [cutover, setCutover] = useState(null);
  const [evals, setEvals] = useState(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    setBusy(true);
    try {
      const ev = await api("/ai/decision-evals/status", { method: "GET" });
      setEvals(ev && typeof ev === "object" ? ev : null);
      if (isAdmin) {
        const c = await api("/ai/gateway/cutover-status", { method: "GET" });
        setCutover(c && typeof c === "object" ? c : null);
      } else {
        setCutover(null);
      }
    } catch (e) {
      toast && toast(softAiSettingsErrorOf(e) || "Gateway / evals status failed", "bad");
    } finally {
      setBusy(false);
    }
  }

  // Re-load when session role arrives Soft≠ invent cutover forever hidden
  useEffect(() => { load(); }, [role]);

  const fams = (cutover && Array.isArray(cutover.enforce_families))
    ? cutover.enforce_families
    : (cutover && cutover.enforce_families ? [...cutover.enforce_families] : []);
  const cal = (evals && evals.calibration) || {};
  const gate = (evals && evals.demote_gate) || {};
  const sampleN = Number(cal.samples || 0);
  const calReady = sampleN > 0 && cal.reason === "ok";
  const accDisp = calReady && cal.accuracy != null ? String(cal.accuracy) : (sampleN ? String(cal.accuracy ?? "—") : "—");
  const eceDisp = calReady && cal.ece != null ? String(cal.ece) : (sampleN ? String(cal.ece ?? "—") : "—");
  const demoteDisp = gate.allowed
    ? "Open"
    : String(gate.reason || cal.reason || "blocked");

  return html`
    <div data-cmc-s13="ops-only">
    <${Card} title="Gateway status"
        sub="Families stay on the deployment. This desk cannot change them. Demote stays closed until soak.">
      <div className="btn-row" style=${{ marginBottom: 8 }}>
        <button type="button" className="btn btn--ghost btn--sm" disabled=${busy}
          onClick=${() => load()}>${busy ? "Loading…" : "Refresh gateway / evals"}</button>
      </div>
      ${isAdmin ? html`
        <div data-cmc-four-visibles="1">
        <${KV} k="Desired vs Effective" v=${cutover && cutover.desired_families && cutover.effective_families
          ? (String((cutover.desired_families || []).length) + "/" + String((cutover.effective_families || []).length) + (cutover.families_ack ? " · ACK" : " · lag"))
          : "—"}/>
        <${KV} k="Enforce families" v=${fams.length ? fams.join(", ") : "none"}/>
        <${KV} k="Still open" v=${cutover && cutover.ungated_remaining != null ? String(cutover.ungated_remaining) : "—"}/>
        <${KV} k="Watch shortcut" v=${cutover && cutover.ai_passthrough_forbidden ? "blocked" : (cutover ? "not set" : "—")}/>
        <${KV} k="Families match" v=${cutover && cutover.families_ack ? "match" : (cutover ? "do not match" : "—")}/>
        <${KV} k="Flag revision" v=${cutover && cutover.flag_revision != null ? String(cutover.flag_revision) : "—"}/>
        <${KV} k="Workers ready" v=${cutover && cutover.flag_ack_n != null
          ? (String(cutover.flag_ack_n) + "/" + String(cutover.flag_ack_expected || 4) + (cutover.flag_lag ? " · waiting" : " · ready"))
          : "—"}/>
        <${KV} k="Flag source" v=${cutover && cutover.flag_sot ? String(cutover.flag_sot) : "—"}/>
        <${KV} k="Households on Watch" v=${cutover && cutover.live_tenant_allowlist_n != null ? (cutover.live_tenant_empty_denies ? "none · deny empty" : String(cutover.live_tenant_allowlist_n)) : "—"}/>
        <${KV} k="Shadow" v=${cutover && cutover.shadow_enabled ? "on" : (cutover ? "off" : "—")}/>
        </div>
      ` : html`<p className="note">Gateway cutover detail is admin+.</p>`}
        <${KV} k="Who decides" v=${evals && evals.decision_owner ? String(evals.decision_owner) : "—"}/>
        <${KV} k="Rules" v=${evals && evals.rules_role ? String(evals.rules_role) : "—"}/>
        <${KV} k="Run state" v=${evals && evals.execution_state ? String(evals.execution_state) : "—"}/>
      <${KV} k="Eval samples" v=${String(cal.samples != null ? cal.samples : "—")}/>
      <${KV} k="Accuracy" v=${accDisp}/>
      <${KV} k="ECE" v=${eceDisp}/>
      <${KV} k="Demote gate" v=${demoteDisp}/>
      <p className="note" style=${{ marginTop: 8 }}>${(evals && evals.note) || "Demote stays closed until soak."}</p>
    <//>
    </div>`;
}

export default AiSettingsDesk;
